import { useEffect, useMemo, useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import { useFormat } from 'common/hooks';
import { AdditionalCurrency } from 'common/hooks/useFormat';
import { useStore } from 'common/store';
import { CurrencyCode, UseFormatValues } from 'common/types/format';
import { UseStoreValues } from 'common/types/store';
import {
  FundExchangeRateContextType
} from 'pages/Waterfall/SpreadsheetConfig/FundExchangeRate/FundExchangeRateContext/FundExchangeRateContext';
import {
  useCreateFundExchangeRateSheet
} from 'pages/Waterfall/SpreadsheetConfig/FundExchangeRate/useCreateFundExchangeRateSheet/useCreateFundExchangeRateSheet';
import {
  UseFundExchangeRateProps
} from 'pages/Waterfall/SpreadsheetConfig/FundExchangeRate/useFundExchangeRate/UseFundExchangeRate.props';
import { getCurrency, worldCurrencies } from 'utilities';
import getExchangeRate from 'utilities/getExchangeRate';

export const useFundExchangeRate = (props: UseFundExchangeRateProps) => {
  const { page, units, measurementDate, marketDate, captable } = props;
  const [{ companyInfo, fundList }] = useStore() as UseStoreValues;
  const currencyCode = getCurrency({ companyInfo, page }) as CurrencyCode;

  const [selectedFundCurrency, setSelectedFundCurrency] = useState<AdditionalCurrency | null>(null);
  const [selectedFundExchangeRate, setSelectedFundExchangeRate] = useState<number | null>(null);

  const investedAdditionalFundCurrencies = useMemo(() => {
    const captableCurrency = companyInfo?.captable_currency as CurrencyCode | undefined;
    const isValidCurrency = (currency: CurrencyCode | undefined): currency is CurrencyCode =>
      currency !== undefined && currency !== null && currency !== '' && currency !== captableCurrency;
    if (captable?.fund_ownership?.fund_ownership_detail && fundList) {
      const fundCurrencySet = new Set(fundList.filter(fund =>
        captable?.fund_ownership?.fund_ownership_detail?.some(fo => fo.fund === fund.id)
      ).map(fund => fund.currency).filter(isValidCurrency));
      return Array.from(fundCurrencySet)
    }
    return [];
  }, [captable, fundList, companyInfo?.captable_currency]);


  // Build currencies locally for Fund Exchange Rate ordering requirements

  const fullCurrencyList = useMemo(() => {
    // Build ordered list per requirements:
    // 1) investedAdditionalFundCurrencies (invested funds with currency != cap table currency)
    // 2) company's cap table currency, financials currency, and USD
    // 3) any other fund currencies
    // 4) the rest of the world currencies

    // Use a Set to preserve insertion order while de-duplicating
    const orderedCodes = new Set<string>();
    const addIfDefined = (code?: string | null) => {
      if (!code) {
        return;
      }
      orderedCodes.add(code);
    };

    // 1) invested additional fund currencies first
    investedAdditionalFundCurrencies.forEach(code => addIfDefined(code));

    // 2) company's currencies and USD
    const captableCurrency = companyInfo?.captable_currency ?? null;
    const financialsCurrency = companyInfo?.financials_currency ?? null;
    addIfDefined(captableCurrency || undefined);
    addIfDefined(financialsCurrency || undefined);
    addIfDefined('USD');

    // 3) other fund currencies
    if (fundList && Array.isArray(fundList)) {
      fundList.forEach(fund => addIfDefined(fund?.currency));
    }

    // 4) rest of world currencies
    Object.keys(worldCurrencies).forEach(code => addIfDefined(code));

    return Array.from(orderedCodes).map(currency => ({
      currency,
      currencyType: 'fund_currency',
      currencyName: currency,
    }));
  }, [investedAdditionalFundCurrencies, companyInfo, fundList]);

  useEffect(() => {
    if (fullCurrencyList) {
      setSelectedFundCurrency(fullCurrencyList?.[0])
    }
  }, [fullCurrencyList]);


  const [format, formatDispatch] = useFormat({
    page,
    units,
    measurementDate,
    marketDate: marketDate ?? undefined,
    additionalCurrencies: fullCurrencyList,
    sourceCurrency: currencyCode,
  }) as unknown as UseFormatValues;
  
  const selectedAdditionalCurrency = useMemo(() => {
    if (format.currency.additionalCurrencies) {
      return format.currency.additionalCurrencies.find(ac => ac.currencyName === selectedFundCurrency?.currencyName) || null;
    }
    return null;
      
  }, [selectedFundCurrency, format]);

  useEffect(() => {
    if (currencyCode && selectedFundCurrency && measurementDate?.id && marketDate) {
      const fromCurrency = currencyCode.toString();
      const toCurrency = selectedFundCurrency.currencyName;
      getExchangeRate(
        fromCurrency,
        toCurrency,
        measurementDate.id.toString(),
        marketDate
      ).then(exchangeRate => {
        if (fromCurrency !== toCurrency && ([1,0,null].includes(exchangeRate))) {
          enqueueSnackbar(`Exchange rate for ${fromCurrency} to ${toCurrency} on ${marketDate} not found`, { variant: 'warning' });
        } else {
          setSelectedFundExchangeRate(exchangeRate as unknown as number);
        }
      }).catch(() => {
        enqueueSnackbar('Error fetching exchange rate', { variant: 'error' });
      }
      )
    }
  }, [currencyCode, measurementDate, marketDate, selectedFundCurrency]);

  const isAdditionalCurrencySelected = useMemo(() => {
    const captableCurrency = companyInfo?.captable_currency;
    return selectedAdditionalCurrency?.currencyName !== captableCurrency;
  }, [companyInfo, selectedAdditionalCurrency])

  const fundExchangeRateProvider = useMemo<FundExchangeRateContextType>(() => ({
    selectedCurrency: selectedAdditionalCurrency,
    setSelectedCurrency: setSelectedFundCurrency,
    exchangeRate: selectedFundExchangeRate,
    currencyList: format.currency.additionalCurrencies,
    standardCurrency: companyInfo?.captable_currency,
  }), [selectedAdditionalCurrency, selectedFundExchangeRate, format, companyInfo])

  const {fundExchangeRateSheet} = useCreateFundExchangeRateSheet()

  return { format, formatDispatch, fundExchangeRateProvider, fundExchangeRateSheet, investedAdditionalFundCurrencies, isAdditionalCurrencySelected };

}